import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInput } from 'semantic-ui-calendar-react'
import { Button, Dropdown, Header, Icon, Popup, Segment, Table } from 'semantic-ui-react'
import cloneDeep from 'lodash.clonedeep'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import styled from 'styled-components'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useParams } from 'react-router-dom'
import { FarmersService } from '~/api/services/farmers/index'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { StyledNoResultsMessage } from '~/components/TableComponent/style'
import CustomIcon from '~/components/CustomIcon'
import Layout from '~/components/Layout'
import { handleErrors } from '~/utils/helpers'

interface FormOFHusbandryProps {
  createdAt: string
  farmer: any
}

const HusbandryTableWrapper = styled.div`
 .field {
  margin: 0 !important;
  opacity: 1 !important;
 }
 .header {
  margin-top: 0em !important;
  padding: 0rem !important;
  font-size: 14px !important;
  background: white !important;
 }
 .ui.fixed.table td {
  overflow: visible !important;
 }
 .segment {
  background: white !important;
 }
 .select-field {
 }

`

const StyledDropdown = styled(Dropdown)`
.menu {
  max-height: 150px !important;
}
`

const FormOFHusbandry: React.FC<FormOFHusbandryProps> = (props) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { languages, logged: { languageId, roleSlug } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [husbandryList, setHusbandryList] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const {
    data: husbandryOptions,
    isLoading: isBusyOptions
  } = FarmersQuery.getFormHusbandryOptions(id)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await FarmersService.getFormHusbandry(id)
      const { husbandryForms } = response.data
      husbandryForms.forEach((_, index) => {
        if (husbandryForms[index].dateFrom) {
          husbandryForms[index].dateFrom = dayjs(husbandryForms[index].dateFrom).format('DD.MM.YYYY')
        }
        if (husbandryForms[index].dateTo) {
          husbandryForms[index].dateTo = dayjs(husbandryForms[index].dateTo).format('DD.MM.YYYY')
        }
      })
      setHusbandryList(husbandryForms)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function getDayBehind(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.subtract(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  function getDayAhead(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.add(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  async function handleUpdate() {
    try {
      if (husbandryList.filter(husbandryForm => husbandryForm.husbandryFormId === '').length > 0) {
        notify(t('Please select husbandry'), 'error')
        return
      }
      setIsBusy(true)
      const tempHusbandryList = cloneDeep(husbandryList)
      tempHusbandryList.forEach((_, index) => {
        if (tempHusbandryList[index].dateFrom) {
          tempHusbandryList[index].dateFrom = parseDate(tempHusbandryList[index].dateFrom)
        }
        if (tempHusbandryList[index].dateTo) {
          tempHusbandryList[index].dateTo = parseDate(tempHusbandryList[index].dateTo)
        }
      })
      await FarmersService.updateFormHusbandry(id, { husbandryForms: tempHusbandryList })
      notify(t('Husbandry form has been successfully updated for the farmer {{farmer}}.', { farmer: `${props.farmer.supplierNo} - ${props.farmer.companyName}` }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderHeader() {
    return <>
      {husbandryList.length === 0 && !isBusy && <Button
        onClick={() => setHusbandryList([...husbandryList, {
          husbandryFormId: '',
          dateFrom: husbandryList.length === 0 ? null : getDayBehind(husbandryList[husbandryList.length - 1].dateTo),
          dateTo: null,
          id: null
        }])}
        style={{ color: 'white' }}
      >{t('Add new husbandry form')}</Button>
      }
      <Button
        disabled={loading}
        onClick={() => handleUpdate()}
        style={{ color: 'white' }}
        type='button'
      >{t('Update')}
      </Button>
    </>
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage className='no-result-message'>
      <Segment placeholder>
        <Header icon>
          <Icon name='search' />
          {t('No results found....')}
        </Header>
        <Segment.Inline>
        </Segment.Inline>
      </Segment>
    </StyledNoResultsMessage>
  }

  function handleDeleteFirstElement() {
    const tempArray = cloneDeep(husbandryList)
    if (tempArray.length > 1) {
      tempArray[1].dateFrom = tempArray[0].dateFrom
    }
    tempArray.splice(0, 1)
    setHusbandryList(tempArray)
  }

  function handleDeleteHusbandryElement(index) {
    const tempArray = cloneDeep(husbandryList)
    tempArray[index + 1].dateFrom = getDayAhead(tempArray[index - 1].dateTo)
    tempArray.splice(index, 1)
    setHusbandryList(tempArray)
  }

  function handleDeleteLastElement(index) {
    const tempArray = cloneDeep(husbandryList)
    tempArray[index - 1].dateTo = ''
    tempArray.splice(index, 1)
    setHusbandryList(tempArray)
  }

  function handleDeleteHusbandry(index) {
    switch (index) {
      case 0:
        return handleDeleteFirstElement()
      case husbandryList.length - 1:
        return handleDeleteLastElement(index)
      default:
        return handleDeleteHusbandryElement(index)
    }
  }

  function renderTableBody() {
    return <>
      {husbandryList?.map((husbandry, index) => {
        const removeDateFromField = index === 0 && !(typeof (husbandry?.dateFrom) === 'string')
        return <Table.Row key={husbandry?.id ?? `husbandry-${Math.random()}`}>
          <Table.Cell>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Valid from')}
              disabled={true}
              clearable
              closable
              maxDate={husbandry.dateTo ? getDayBehind(husbandry.dateTo) : ''}
              initialDate={husbandry.dateTo ? getDayBehind(husbandry.dateTo) : ''}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={removeDateFromField ? dayjs(props.createdAt).format('DD.MM.YYYY') : husbandry.dateFrom}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                const tempHusbandryList = cloneDeep(husbandryList)
                tempHusbandryList[index].dateFrom = value
                setHusbandryList(tempHusbandryList)
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <DateInput
              name='dateTime'
              onKeyPress={handleKeyPress}
              placeholder={t('Open')}
              clearable
              closable
              minDate={husbandry.dateFrom ? getDayAhead(husbandry.dateFrom) : ''}
              disabled={index !== husbandryList.length - 1 || lightUserLogged}
              initialDate={husbandry.dateFrom ? getDayAhead(husbandry.dateFrom) : ''}
              autoComplete='off'
              markColor={'blue'}
              localization={languageLocal.code}
              closeOnMouseLeave={false}
              dateFormat={'DD.MM.YYYY'}
              className={'datePicker'}
              value={husbandry.dateTo || ''}
              iconPosition='left'
              onChange={(event, { name, value }) => {
                const tempHusbandryList = cloneDeep(husbandryList)
                tempHusbandryList[index].dateTo = value
                const newDateFrom = value ? tempHusbandryList[index].dateTo : tempHusbandryList[index].dateFrom
                setHusbandryList([...tempHusbandryList, {
                  husbandryFormId: '',
                  dateFrom: getDayAhead(newDateFrom),
                  dateTo: null,
                  id: null
                }])
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <StyledDropdown
              selection
              floating
              upward
              disabled={lightUserLogged}
              placeholder={t('Husbandry forms')}
              selectOnBlur={false}
              scrolling
              value={husbandry.husbandryFormId}
              options={husbandryOptions?.husbandryForms?.map(husbandryOption => ({ text: husbandryOption.name, value: husbandryOption.id })) || []}
              onChange={(e, data) => {
                const tempHusbandryList = cloneDeep(husbandryList)
                tempHusbandryList[index].husbandryFormId = data.value
                setHusbandryList(tempHusbandryList)
              }
              }
            />
          </Table.Cell>
          {!lightUserLogged && <Table.Cell>
            <Popup
              content={t('Delete')}
              trigger={<CustomIcon
                color='red'
                name='trash'
                onClick={() => handleDeleteHusbandry(index)}
                style={{ fontSize: '1.2em' }}
              />}
            />
          </Table.Cell>}
        </Table.Row>
      })}
    </>
  }

  function renderTable() {
    return <Table celled fixed singleLine>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Validity period from')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Validity period to')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Form of husbandry')}</Table.HeaderCell>
          {!lightUserLogged && <Table.HeaderCell width={'2'}>{t('Actions')}</Table.HeaderCell>}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table >
  }

  const loading = isBusy || isBusyOptions

  return <Layout
    title={t('Form of husbandry')}
    childContainerSize={'auto'}
    useSpinner
    isBusy={isBusyOptions || isBusy}
    childrenOverflow={'visible'}
    actions={!lightUserLogged && renderHeader()}
  >
    <HusbandryTableWrapper>
      {renderTable()}
      {husbandryList.length === 0 && !isBusy && renderNoResultsMessage()}
    </HusbandryTableWrapper>
  </Layout>
}
export default FormOFHusbandry